// Planification des intrants : besoins calculés (planning de production × consommation par poteau), commandes fournisseurs
// attendues, projection du stock semaine par semaine et ruptures prévues.
import React, { useCallback, useEffect, useState } from 'react';
import RecettesDialog from './RecettesDialog';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Switch, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FileDownloadOutlined, PaymentsOutlined, TodayOutlined, TuneOutlined, UploadFileOutlined } from '@mui/icons-material';
import { Panel } from '../../components/ui';
import { planningAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { tokens } from '../../theme';
import { aujourdhui, lundiDe, plusJours } from './common';

const nf = (n, d = 1) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: d }));
const fr = (x) => (x ? x.slice(8, 10) + '/' + x.slice(5, 7) : '');
const COUL = { RUPTURE: 'error.main', ALERTE: 'warning.main', OK: undefined };

// Colonne « Type » figée à gauche : elle reste visible quand on fait défiler les intrants
const colType = { position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', borderRight: 1, borderRightColor: 'divider' };

function NomenclatureDialog({ open, d, onClose, onSaved }) {
  const [v, setV] = useState({});
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open || !d) return;
    setError('');
    const x = {};
    (d.intrants.filter((i) => i.pour_pba !== false)).forEach((i) => Object.entries(i.nomenclature).forEach(([ty, q]) => { x[`${ty}|${i.intrant_id}`] = String(q).replace('.', ','); }));
    setV(x);
  }, [open, d]);
  if (!d) return null;
  const articles = d.intrants.filter((i) => i.pour_pba !== false); // le ciment 32,5 R (briques, pavés) n'entre pas dans un poteau
  const save = async () => {
    setError('');
    try {
      const valeurs = d.types.flatMap((ty) => articles.map((i) => ({ pba_type: ty, intrant_id: i.intrant_id, quantite: v[`${ty}|${i.intrant_id}`] || '' })));
      const r = await planningAPI.nomenclature(valeurs); onSaved(r.data.message);
    } catch (e) { setError(apiError(e)); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Consommation par poteau (nomenclature)</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.5 }}>
          Quantité de chaque intrant pour <b>un</b> poteau, dans l'unité du stock (kg de fer, sacs de ciment, m³ de béton 3/8, 8/16, 0/3 et 16/25, m³ de sable). Laissez vide si le type n'en utilise pas.
          La ligne « Réel 90 j » donne la consommation moyenne constatée par poteau (tous types confondus) d'après les rapports journaliers, comme repère.
        </Typography>
        <TableContainer sx={{ maxHeight: '60vh' }}>
          <Table size="small" stickyHeader sx={{ '& td, & th': { px: 0.75 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...colType, zIndex: 3 }}>Type</TableCell>
                {articles.map((i) => <TableCell key={i.intrant_id} align="center" sx={{ minWidth: 86, fontSize: 12 }}>{i.designation}<br /><span style={{ fontWeight: 400 }}>({i.unite})</span></TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ ...colType, bgcolor: 'background.default', fontSize: 12.5, fontStyle: 'italic' }}>Réel 90 j</TableCell>
                {articles.map((i) => <TableCell key={i.intrant_id} align="center" sx={{ fontSize: 12.5, fontFamily: tokens.mono, color: 'text.secondary' }}>{i.histo_par_poteau ? nf(i.histo_par_poteau, 2) : '—'}</TableCell>)}
              </TableRow>
              {d.types.map((ty) => (
                <TableRow key={ty}>
                  <TableCell sx={{ ...colType, fontWeight: 700, whiteSpace: 'nowrap' }}>{ty}</TableCell>
                  {articles.map((i) => {
                    const k = `${ty}|${i.intrant_id}`;
                    return (
                      <TableCell key={i.intrant_id} align="center">
                        <TextField variant="standard" value={v[k] || ''} placeholder="—" sx={{ width: 70 }}
                          inputProps={{ inputMode: 'decimal', style: { textAlign: 'center' }, 'aria-label': `${ty} ${i.designation}` }}
                          onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value.replace(/[^\d,.]/g, '') }))} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button><Button variant="contained" onClick={save}>Enregistrer</Button></DialogActions>
    </Dialog>
  );
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const titrePeriode = (p, mois) => (mois ? `${MOIS[Number(p.debut.slice(5, 7)) - 1]} ${p.debut.slice(0, 4)}` : `Sem. du ${fr(p.debut)}`);
const fcfa = (n) => (n === null || n === undefined ? '—' : `${Math.round(n).toLocaleString('fr-FR')} F`);
const moisDecale = (d, n) => { const x = new Date(`${d.slice(0, 7)}-01T00:00:00Z`); x.setUTCMonth(x.getUTCMonth() + n); return x.toISOString().slice(0, 10); };

/** Un article par nom (et unité) : les doublons de désignation ne forment qu'une ligne, le prix s'applique à tous. */
const cleArticle = (i) => `${i.designation.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()}|${String(i.unite).toLowerCase()}`;
function articlesUniques(intrants) {
  const m = new Map();
  intrants.forEach((i) => {
    const k = cleArticle(i);
    const g = m.get(k) || { cle: k, designation: i.designation, unite: i.unite, ids: [], prix_reference: null, prix_dernier_bc: null };
    g.ids.push(i.intrant_id);
    if (g.prix_reference === null && i.prix_reference !== null) g.prix_reference = i.prix_reference;
    if (g.prix_dernier_bc === null && i.prix_dernier_bc !== null) g.prix_dernier_bc = i.prix_dernier_bc;
    m.set(k, g);
  });
  return [...m.values()];
}

function ReglagesDialog({ open, d, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const articles = d ? articlesUniques(d.intrants) : [];
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open || !d) return;
    setError('');
    setF({ securite_jours: String(d.param.securite_jours), delai_jours: String(d.param.delai_jours),
      prix: Object.fromEntries(articlesUniques(d.intrants).map((a) => [a.cle, a.prix_reference !== null ? String(a.prix_reference).replace('.', ',') : ''])) });
  }, [open, d]);
  if (!f) return null;
  const save = async () => {
    setError('');
    try {
      const r = await planningAPI.parametresIntrants({ securite_jours: f.securite_jours, delai_jours: f.delai_jours,
        prix: articles.flatMap((a) => a.ids.map((id) => ({ intrant_id: id, prix: f.prix[a.cle] }))) });
      onSaved(r.data.message);
    } catch (e) { setError(apiError(e)); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Réglages des commandes et prix des intrants</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <TextField label="Stock de sécurité (jours de production)" value={f.securite_jours} onChange={(e) => setF({ ...f, securite_jours: e.target.value })} sx={{ flex: 1 }} inputProps={{ inputMode: 'decimal' }} />
          <TextField label="Délai de commande (jours)" value={f.delai_jours} onChange={(e) => setF({ ...f, delai_jours: e.target.value })} sx={{ flex: 1 }} inputProps={{ inputMode: 'numeric' }}
            helperText="Traitement + livraison fournisseur" />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 2, mb: 0.5 }}>Prix de référence (FCFA HT, transport compris, par unité de stock)</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1 }}>Laissez vide pour utiliser le prix du dernier bon de commande fournisseur.</Typography>
        <Table size="small">
          <TableBody>
            {articles.map((i) => (
              <TableRow key={i.cle}>
                <TableCell>{i.designation}</TableCell>
                <TableCell sx={{ width: 170 }}>
                  <TextField size="small" variant="standard" value={f.prix[i.cle] ?? ''} placeholder={i.prix_dernier_bc ? `BC : ${nf(i.prix_dernier_bc, 2)}` : '—'}
                    onChange={(e) => setF({ ...f, prix: { ...f.prix, [i.cle]: e.target.value.replace(/[^\d,.\s]/g, '') } })}
                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' }, 'aria-label': `Prix ${i.designation}` }}
                    InputProps={{ endAdornment: <Typography sx={{ fontSize: 12, ml: 0.5, whiteSpace: 'nowrap' }}>F / {i.unite}</Typography> }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button><Button variant="contained" onClick={save}>Enregistrer</Button></DialogActions>
    </Dialog>
  );
}

const Intrants = ({ onInfo }) => {
  const [mois, setMois] = useState(false);
  const [debut, setDebut] = useState(lundiDe(aujourdhui()));
  const [nb, setNb] = useState(4);
  const [d, setD] = useState(null);
  const [tous, setTous] = useState(false);
  const [nomen, setNomen] = useState(false);
  const [recettes, setRecettes] = useState(false);
  const [reglages, setReglages] = useState(false);
  const [error, setError] = useState('');
  const charger = useCallback(() => planningAPI.intrants(debut, nb, mois ? 'mois' : 'semaine').then((r) => setD(r.data)).catch((e) => setError(apiError(e))), [debut, nb, mois]);
  useEffect(() => { charger(); }, [charger]);
  const changerVue = (m) => {
    if (m === null || m === mois) return;
    setMois(m); setNb(m ? 3 : 4); setDebut(m ? `${aujourdhui().slice(0, 7)}-01` : lundiDe(aujourdhui()));
  };
  const decaler = (n) => setDebut(mois ? moisDecale(debut, n) : plusJours(debut, 7 * n));

  const concernes = (d?.intrants || []).filter((i) => tous || i.besoin_total > 0 || i.appros.length || Object.keys(i.nomenclature).length);
  const ruptures = (d?.intrants || []).filter((i) => i.premiere_rupture);
  const alertes = (d?.intrants || []).filter((i) => !i.premiere_rupture && i.premiere_alerte);
  const appros = (d?.intrants || []).flatMap((i) => i.appros.map((a) => ({ ...a, unite: i.unite, article: i.designation })));
  const vide = d && !d.intrants.some((i) => Object.keys(i.nomenclature).length);
  const aCommander = concernes.filter((i) => i.commande_total > 0);
  const t = aujourdhui();

  const exporter = async () => {
    const P = d.periodes;
    const lignes = [
      [{ value: 'Projection du stock (sans nouvelle commande)', fontWeight: 'bold' }],
      ['Intrant', 'Unité', 'Stock actuel', ...P.flatMap((p) => [`Besoin ${titrePeriode(p, mois)}`, `Appro ${titrePeriode(p, mois)}`, `Stock fin ${titrePeriode(p, mois)}`]), 'Manque'],
      ...concernes.map((i) => [i.designation, i.unite, i.stock, ...i.periodes.flatMap((s) => [s.besoin, s.appro, s.stock_fin]), i.manque]),
      [],
      [{ value: `Commandes à prévoir et budget (sécurité ${d.param.securite_jours} j, délai ${d.param.delai_jours} j)`, fontWeight: 'bold' }],
      ['Intrant', 'Unité', 'Prix (F)', ...P.flatMap((p) => [`Qté ${titrePeriode(p, mois)} (avant le ${fr(p.date_limite)})`, `Montant ${titrePeriode(p, mois)}`]), 'Qté totale', 'Montant total'],
      ...concernes.map((i) => [i.designation, i.unite, i.prix, ...i.periodes.flatMap((s) => [s.commande, s.montant]), i.commande_total, i.montant_total]),
      ['TOTAL', '', '', ...P.flatMap((p) => [null, p.budget]), null, d.budget_total],
    ];
    await ecrireExcel(lignes, `planning-intrants-${d.debut}.xlsx`, { largeurs: [26, 8, 12, ...P.flatMap(() => [16, 14, 14]), 12, 14] });
  };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {vide && <Alert severity="info" sx={{ mb: 2 }} action={d.modifiable && <Button color="inherit" onClick={() => setRecettes(true)}>Reprendre d'un Excel</Button>}>
        Pour calculer les besoins, indiquez la consommation d'un poteau de chaque type : bouton « Consommation par poteau », ou reprenez les recettes de votre fichier Excel (onglet INTRANT).</Alert>}
      {ruptures.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <b>Rupture prévue sans nouvelle commande :</b> {ruptures.map((i) => `${i.designation} (${mois ? titrePeriode({ debut: i.premiere_rupture }, true) : `semaine du ${fr(i.premiere_rupture)}`}, manque ${nf(i.manque)} ${i.unite})`).join(' · ')}
        </Alert>
      )}
      {alertes.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}><b>Sous le seuil d'alerte :</b> {alertes.map((i) => `${i.designation} (${fr(i.premiere_alerte)})`).join(' · ')}</Alert>}
      {d?.sans_nomenclature.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>Production planifiée sans consommation renseignée pour : <b>{d.sans_nomenclature.join(', ')}</b> — ses besoins ne sont pas comptés.</Alert>}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={mois} onChange={(e, v) => changerVue(v)}>
            <ToggleButton value={false}>Semaines</ToggleButton><ToggleButton value>Mois</ToggleButton>
          </ToggleButtonGroup>
          <IconButton aria-label="Période précédente" onClick={() => decaler(-1)}><ChevronLeft /></IconButton>
          <Typography sx={{ fontWeight: 700, minWidth: 150, textAlign: 'center' }}>{d ? `À partir ${mois ? 'de' : 'du'} ${mois ? titrePeriode(d.periodes[0], true) : fr(d.debut)}` : ''}</Typography>
          <IconButton aria-label="Période suivante" onClick={() => decaler(1)}><ChevronRight /></IconButton>
          <Button size="small" startIcon={<TodayOutlined />} onClick={() => setDebut(mois ? `${t.slice(0, 7)}-01` : lundiDe(t))}>{mois ? 'Ce mois' : 'Cette semaine'}</Button>
          <TextField select size="small" label="Horizon" value={nb} onChange={(e) => setNb(e.target.value)} sx={{ width: 130 }}>
            {(mois ? [1, 2, 3, 6] : [2, 4, 8, 12]).map((n) => <MenuItem key={n} value={n}>{n} {mois ? 'mois' : 'semaines'}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Switch checked={tous} onChange={(e) => setTous(e.target.checked)} />} label="Tous les intrants" />
          <Box sx={{ flex: 1 }} />
          {d && <Button startIcon={<FileDownloadOutlined />} onClick={() => exporter().catch((e) => setError(e.message))}>Excel</Button>}
          {d?.modifiable && <Button startIcon={<PaymentsOutlined />} onClick={() => setReglages(true)}>Réglages et prix</Button>}
          {d?.modifiable && <Button startIcon={<UploadFileOutlined />} onClick={() => setRecettes(true)}>Reprendre d'un Excel</Button>}
          <Button variant={vide ? 'contained' : 'outlined'} startIcon={<TuneOutlined />} onClick={() => setNomen(true)} disabled={!d}>Consommation par poteau</Button>
        </Box>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2}>Intrant</TableCell>
                <TableCell rowSpan={2} align="right">Stock actuel</TableCell>
                {d?.periodes.map((s) => <TableCell key={s.debut} align="center" sx={{ borderLeft: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>{titrePeriode(s, mois)}<Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{s.poteaux} poteaux planifiés</Typography></TableCell>)}
                <TableCell rowSpan={2} align="right" sx={{ borderLeft: '1px solid', borderColor: 'divider' }}>Manque</TableCell>
              </TableRow>
              <TableRow>{d?.periodes.map((s) => <TableCell key={s.debut} align="center" sx={{ fontSize: 11.5, color: 'text.secondary', borderLeft: '1px solid', borderColor: 'divider' }}>besoin · appro → stock</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {concernes.map((i) => (
                <TableRow key={i.intrant_id} hover>
                  <TableCell><b>{i.designation}</b><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{i.unite}{i.seuil ? ` · seuil ${nf(i.seuil)}` : ''}</Typography></TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{nf(i.stock)}</TableCell>
                  {i.periodes.map((s) => (
                    <TableCell key={s.debut} align="center" sx={{ borderLeft: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', fontFamily: tokens.mono, fontSize: 13 }}>
                      <span style={{ opacity: s.besoin ? 1 : 0.45 }}>{s.besoin ? `−${nf(s.besoin)}` : '—'}</span>{s.appro ? <span style={{ color: '#1D7A46' }}> +{nf(s.appro)}</span> : ''}
                      <Typography component="div" sx={{ fontFamily: tokens.mono, fontWeight: 700, fontSize: 13, color: COUL[s.statut] }}>{nf(s.stock_fin)}</Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ borderLeft: '1px solid', borderColor: 'divider', fontFamily: tokens.mono, color: i.manque ? 'error.main' : 'text.disabled', fontWeight: i.manque ? 700 : 400 }}>{i.manque ? nf(i.manque) : '—'}</TableCell>
                </TableRow>
              ))}
              {d && !concernes.length && <TableRow><TableCell colSpan={20} sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>Aucun besoin calculé sur la période (pas de production planifiée ou pas de consommation par poteau renseignée).</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 3, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700 }}>Commandes à prévoir et budget</Typography>
        {d && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>besoin + {nf(d.param.securite_jours)} j de sécurité − stock − commandes attendues · délai {d.param.delai_jours} j</Typography>}
        <Box sx={{ flex: 1 }} />
        {d && <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{fcfa(d.budget_total)}</Typography>}
      </Box>
      {d?.sans_prix.length > 0 && <Alert severity="warning" sx={{ mb: 1.5 }}>Sans prix (non comptés dans le budget) : <b>{d.sans_prix.join(', ')}</b>{d.modifiable ? ' — renseignez-les dans « Réglages et prix ».' : '.'}</Alert>}
      <Panel flush>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Intrant</TableCell>
                <TableCell align="right">Prix</TableCell>
                {d?.periodes.map((p) => (
                  <TableCell key={p.debut} align="center" sx={{ borderLeft: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>{titrePeriode(p, mois)}
                    <Typography sx={{ fontSize: 11.5, color: p.date_limite < t ? 'error.main' : 'text.secondary', fontWeight: p.date_limite < t ? 700 : 400 }}>
                      {p.date_limite < t ? 'à commander sans attendre' : `à commander avant le ${fr(p.date_limite)}`}</Typography>
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ borderLeft: '1px solid', borderColor: 'divider' }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aCommander.map((i) => (
                <TableRow key={i.intrant_id} hover>
                  <TableCell><b>{i.designation}</b><Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{i.unite}</Typography></TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, whiteSpace: 'nowrap', cursor: d.modifiable ? 'pointer' : undefined, '&:hover': d.modifiable ? { color: 'primary.main', textDecoration: 'underline' } : {} }}
                    title={d.modifiable ? 'Modifier les prix' : undefined} onClick={d.modifiable ? () => setReglages(true) : undefined}>{i.prix !== null ? `${nf(i.prix, 2)} F / ${i.unite}` : '—'}
                    {i.prix_source === 'bc' && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>dernier BC</Typography>}</TableCell>
                  {i.periodes.map((s) => (
                    <TableCell key={s.debut} align="center" sx={{ borderLeft: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', fontFamily: tokens.mono, fontSize: 13 }}>
                      {s.commande ? <><b>{nf(s.commande)}</b><Typography sx={{ fontSize: 11.5, fontFamily: tokens.mono, color: 'text.secondary' }}>{fcfa(s.montant)}</Typography></> : <span style={{ opacity: 0.4 }}>—</span>}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ borderLeft: '1px solid', borderColor: 'divider', fontFamily: tokens.mono, whiteSpace: 'nowrap' }}><b>{nf(i.commande_total)}</b>
                    <Typography sx={{ fontSize: 11.5, fontFamily: tokens.mono, color: 'text.secondary' }}>{fcfa(i.montant_total)}</Typography></TableCell>
                </TableRow>
              ))}
              {d && aCommander.length > 0 && (
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell>Budget</TableCell><TableCell />
                  {d.periodes.map((p) => <TableCell key={p.debut} align="center" sx={{ fontFamily: tokens.mono, borderLeft: '1px solid', borderColor: 'divider' }}>{fcfa(p.budget)}</TableCell>)}
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, borderLeft: '1px solid', borderColor: 'divider' }}>{fcfa(d.budget_total)}</TableCell>
                </TableRow>
              )}
              {d && !aCommander.length && <TableRow><TableCell colSpan={20} sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>Rien à commander sur la période : le stock et les commandes attendues couvrent les besoins et le stock de sécurité.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Approvisionnements attendus (bons de commande fournisseurs envoyés)</Typography>
      <Panel flush>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>BC</TableCell><TableCell>Fournisseur</TableCell><TableCell>Article</TableCell><TableCell align="right">Reste à recevoir</TableCell><TableCell>Livraison prévue</TableCell></TableRow></TableHead>
            <TableBody>
              {appros.map((a) => (
                <TableRow key={`${a.bc_id}-${a.intrant_id}-${a.designation}`}>
                  <TableCell sx={{ fontFamily: tokens.mono }}>{a.numero}{a.statut === 'PARTIEL' && <Chip size="small" label="partiel" variant="outlined" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{a.fournisseur}</TableCell>
                  <TableCell>{a.article}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{nf(a.reste)} {a.unite}</TableCell>
                  <TableCell>{a.date_prevue ? a.date_prevue.split('-').reverse().join('/') : '—'}
                    {a.en_retard && <Chip size="small" color="error" label="en retard" sx={{ ml: 1 }} />}
                    {a.sans_date && <Chip size="small" variant="outlined" label="sans date : comptée dans la 1re période" sx={{ ml: 1 }} />}
                    {a.hors_horizon && <Chip size="small" variant="outlined" label="après l'horizon" sx={{ ml: 1 }} />}</TableCell>
                </TableRow>
              ))}
              {d && !appros.length && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>Aucune commande fournisseur en attente de réception.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
        Le stock de départ est le stock réel du stock intrants (pas un stock théorique). Projection : stock + commandes attendues − besoins (production planifiée restant à faire × consommation par poteau) ;
        en rouge : rupture, en orange : sous le seuil d'alerte. Les responsables et les achats reçoivent chaque matin une alerte si une rupture est prévue dans les 3 semaines.
      </Typography>
      {d?.modifiable && <NomenclatureDialog open={nomen} d={d} onClose={() => setNomen(false)} onSaved={(m) => { setNomen(false); onInfo(m); charger(); }} />}
      {d?.modifiable && <RecettesDialog open={recettes} d={d} onClose={() => setRecettes(false)} onSaved={(m) => { setRecettes(false); onInfo(m); charger(); }} />}
      {d?.modifiable && <ReglagesDialog open={reglages} d={d} onClose={() => setReglages(false)} onSaved={(m) => { setReglages(false); onInfo(m); charger(); }} />}
      {d && !d.modifiable && nomen && (
        <Dialog open onClose={() => setNomen(false)}><DialogTitle>Consommation par poteau</DialogTitle>
          <DialogContent>Seuls l'administrateur et le responsable de production modifient la nomenclature.</DialogContent>
          <DialogActions><Button onClick={() => setNomen(false)}>Fermer</Button></DialogActions></Dialog>
      )}
    </>
  );
};

export default Intrants;
